/**
 * @file Table rows for the back-ordered lines on the back order detail page.
 */



import React from "react";
import { TableCell, TableRow } from "@mui/material";
import { blueGrey } from "@mui/material/colors";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";



/**
 * Styles for the row text: blue-grey text (shade 600).
 */
const useStyles = makeStyles({
  root: {
    color: blueGrey[600],
  },
});



/**
 * Renders one page of back-ordered delivery lines as table rows.
 *
 * Meant to be placed inside a `PagedTable`, which supplies the table, header and pagination controls.
 * This component only renders the rows for the current page.
 *
 * Each row shows, in order:
 * item code, warehouse, unit price, quantity ordered, ordered total, quantity back-ordered and back-ordered total.
 * Money values are shown as "$" with 2 decimal places, and quantities are rounded to whole numbers.
 *
 * @param {Object} props
 * @param {Object[]} props.items - The back-ordered lines, from the `backOrderItems` field of the `/backorders/<id>` response.
 *                                 Each needs `itemNumber`, `itemCode`, `warehouse`, `price`, `ordered`, `orderedTotal`, `backOrdered` and `backOrderedTotal`.
 * @param {number} props.currentPage - The zero-based page number to show.
 * @param {number} props.pageSize - Rows per page, or -1 to show all rows.
 * @returns {JSX.Element} The table rows for the current page.
 */
function BackOrderDetailsList({ items, currentPage, pageSize }) {
  const classes = useStyles();
  const endIndex = pageSize === -1 ? items.length : currentPage * pageSize + pageSize;

  return (
    <React.Fragment>
      {items.slice(currentPage * pageSize, endIndex).map((item) => (
        <TableRow key={item.itemNumber}>
          <TableCell>
            <Typography className={classes.root} variant="body2">
              {item.itemCode}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography className={classes.root} variant="body2">
              {item.warehouse}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography className={classes.root} variant="body2">
              {"$" + (item.price * 1).toFixed(2)}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography className={classes.root} variant="body2">
              {(item.ordered * 1).toFixed(0)}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography className={classes.root} variant="body2">
              {"$" + (item.orderedTotal * 1).toFixed(2)}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography className={classes.root} variant="body2">
              {(item.backOrdered * 1).toFixed(0)}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography className={classes.root} variant="body2">
              {"$" + (item.backOrderedTotal * 1).toFixed(2)}
            </Typography>
          </TableCell>
        </TableRow>
      ))}
    </React.Fragment>
  );
}

export default BackOrderDetailsList;